import AddIcon from "@mui/icons-material/Add";
import AddTaskIcon from "@mui/icons-material/AddTask";
import Logger from "../../../../services/Logger";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import DeleteIcon from "@mui/icons-material/Delete";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ApiService from "../../../../services/ApiService";
import DictionaryApiService from "../../../system/integrate/DictionaryApiService";
import AdminGridBoardService, { DRAFT } from "../AdminGridBoardService";
import ConfirmDialog from "../../../components/ConfirmDialog";
import GirdCanAssocConfirmDialog from "./GirdCanAssocConfirmDialog";
import GridCandEntry from "./GridCandEntry";
import WordsNotInUseFrenchNotice from "../../../pzl_common/WordsNotInUseFrenchNotice";
import { findEmptySpaces, generatePositions } from "./GridCandUtils";
import { resolveTplGridDims, resolveTplGridSize } from "../gridTplSize";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

export default function GridCanAssoc({ gridTpl, square, onWordAdd, onShowSnackbar, onWordSelect, onRefresh, highlightedWordFromPreview = null, placementWordFromFill = null, onClearPlacementWordFromFill }) {
    const { rows: gridRowsCount, cols: gridColsCount } = resolveTplGridDims(gridTpl);
    const gridSize = resolveTplGridSize(gridTpl);
    const gridCellCount = gridRowsCount * gridColsCount;
    const isFrenchPuzzle = String(gridTpl?.language ?? "").toLowerCase() === "french";
    const [puzzleWords, setPuzzleWords] = useState([]);
    const [wordsNotInUse, setWordsNotInUse] = useState([]);
    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedAssociatedWord, setSelectedAssociatedWord] = useState(null);
    const [associatedWords, setAssociatedWords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [wordToPlace, setWordToPlace] = useState(null);
    const [selectedWordForPlacement, setSelectedWordForPlacement] = useState(null);
    const [emptySpaces, setEmptySpaces] = useState([]);
    const [candidateWords, setCandidateWords] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [deleteWordDialogOpen, setDeleteWordDialogOpen] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);
    const [filterByLength, setFilterByLength] = useState(false);
    const [filterWordsNotInUseByLength, setFilterWordsNotInUseByLength] = useState(false);
    const [filterBedrockByLength, setFilterBedrockByLength] = useState(false);
    const [bedrockCandidates, setBedrockCandidates] = useState([]);
    const [loadingBedrock, setLoadingBedrock] = useState(false);
    const [addAllWordsDialogOpen, setAddAllWordsDialogOpen] = useState(false);

    // Check for empty spaces when square changes
    useEffect(() => {
        if (square && square.length > 0) {
            const spaces = findEmptySpaces(square);
            setEmptySpaces(spaces);
        } else {
            setEmptySpaces([]);
        }
    }, [square]);

    // Extract words from filledCoordinates
    useEffect(() => {
        if (!gridTpl?.filledCoordinates) {
            setPuzzleWords([]);
            return;
        }
        try {
            const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
            const words = filledCoordinates
                .filter(word => word.text) // Filter out words without text
                .map((word) => {
                    return word.text;
                });
            // Sort words alphabetically
            const sortedWords = [...words].sort((a, b) => a.localeCompare(b));
            setPuzzleWords(sortedWords);
        } catch (error) {
            Logger.error("Error parsing filledCoordinates:", error);
            setPuzzleWords([]);
        }
    }, [gridTpl?.filledCoordinates]);

    // Extract words from wordsNotInUse
    useEffect(() => {
        if (!gridTpl?.wordsNotInUse) {
            setWordsNotInUse([]);
            return;
        }
        try {
            const words = gridTpl.wordsNotInUse
                .split('\n')
                .map(word => word.trim())
                .filter(word => word.length > 0);
            // Sort words alphabetically
            const sortedWords = [...words].sort((a, b) => a.localeCompare(b));
            setWordsNotInUse(sortedWords);
        } catch (error) {
            Logger.error("Error parsing wordsNotInUse:", error);
            setWordsNotInUse([]);
        }
    }, [gridTpl?.wordsNotInUse]);

    // When Fill dialog requests placement, open the Place Word in Puzzle dialog
    useEffect(() => {
        if (placementWordFromFill) {
            setSelectedWordForPlacement(placementWordFromFill);
            setWordToPlace(placementWordFromFill);
            setConfirmDialogOpen(true);
        }
    }, [placementWordFromFill]);

    // Helper function to find candidate words for a specific word
    const findCandidateWordsForWord = async (puzzleWord) => {
        if (!puzzleWord) {
            setCandidateWords([]);
            return;
        }

        // Calculate filled cells inline to avoid dependency issues
        let filledCellsCount = 0;
        if (gridTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                const uniqueCells = new Set();
                filledCoordinates.forEach(word => {
                    if (word.text && word.positions) {
                        word.positions.forEach(pos => {
                            uniqueCells.add(`${pos.row}-${pos.col}`);
                        });
                    }
                });
                filledCellsCount = uniqueCells.size;
            } catch (error) {
                Logger.error("Error calculating filled cells:", error);
            }
        }

        const emptyCellCount = gridCellCount - filledCellsCount;
        if (emptyCellCount <= 0) {
            setCandidateWords([]);
            return;
        }

        setLoadingCandidates(true);
        try {
            // Get used words set
            const usedWords = new Set(puzzleWords.map(w => w.toUpperCase()));

            // Collect candidate words from synonyms and associations for the selected word
            const allCandidates = new Map(); // Use Map to avoid duplicates

            try {
                const language = gridTpl?.language || 'english';
                
                // Get associations
                const result = await DictionaryApiService.getWordAssociations(puzzleWord, language);
                const associations = result.relatedWords || [];
                Logger.log(`Found ${associations.length} associations for "${puzzleWord}"`);
                associations.forEach(item => {
                    const word = item.word.toUpperCase();
                    if (!usedWords.has(word) && word.length <= emptyCellCount) {
                        if (!allCandidates.has(word)) {
                            allCandidates.set(word, {
                                word: word,
                                source: puzzleWord,
                                type: 'association',
                                score: item.score || 0
                            });
                        }
                    }
                });
            } catch (error) {
                Logger.error(`Error finding candidates for "${puzzleWord}":`, error);
            }

            Logger.log(`Total candidates collected: ${allCandidates.size}`);
            Logger.log(`Empty cell count: ${emptyCellCount}`);

            // Convert to array, sort by word length (descending), and limit to 10
            const candidatesArray = Array.from(allCandidates.values())
                .sort((a, b) => b.word.length - a.word.length)
                .slice(0, 10);

            setCandidateWords(candidatesArray);
        } catch (error) {
            Logger.error("Error finding candidate words:", error);
            setCandidateWords([]);
        } finally {
            setLoadingCandidates(false);
        }
    };

    // Handle word selection and search for associations
    const handleWordSelect = async (word) => {
        setSelectedWord(word);
        setSelectedAssociatedWord(null); // Clear associated word selection
        setLoading(true);
        setAssociatedWords([]);
        setCandidateWords([]); // Clear previous candidates

        // Find the word data from filledCoordinates for highlighting
        let wordDataForHighlighting = null;
        if (gridTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                
                // Find word by matching text
                wordDataForHighlighting = filledCoordinates.find(w => {
                    if (!w.text) return false;
                    return w.text.toUpperCase() === word.toUpperCase();
                });
            } catch (error) {
                Logger.error("Error parsing filled coordinates:", error);
            }
        }

        // Notify parent component about puzzle word selection
        if (onWordSelect) {
            onWordSelect(wordDataForHighlighting || { text: word });
        }

        // Find candidate words for the selected word
        await findCandidateWordsForWord(word);

        try {
            const language = gridTpl?.language || 'english';
            
            // Search for associated words using Dictionary API
            const result = await DictionaryApiService.getWordAssociations(word.toLowerCase(), language);
            const results = result.relatedWords || [];

            if (results && results.length > 0) {
                Logger.log(`Found ${results.length} associations for "${word}"`);
                
                // Filter out words that are already used in the puzzle
                let usedWords = new Set();
                if (gridTpl?.filledCoordinates) {
                    try {
                        const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                        const words = filledCoordinates
                            .filter(w => w.text) // Filter out words without text
                            .map(w => {
                                // Remove hyphens for comparison
                                const text = w.text || '';
                                return text.replace(/-/g, '').toUpperCase();
                            });
                        usedWords = new Set(words);
                    } catch (error) {
                        Logger.error("Error parsing filledCoordinates:", error);
                    }
                }
                
                // Filter results - compare without hyphens
                const filteredResults = results.filter(result => {
                    if (!result || !result.word) return false;
                    const resultWordWithoutHyphens = result.word.replace(/-/g, '').toUpperCase();
                    return !usedWords.has(resultWordWithoutHyphens);
                });

                Logger.log(`After filtering used words: ${filteredResults.length} words remaining`);

                // Check if each word can fit in the puzzle
                const wordsWithPositions = await checkWordFitInPuzzle(filteredResults);
                Logger.log(`Words with positions: ${wordsWithPositions.length}`);
                setAssociatedWords(wordsWithPositions);
            } else {
                setAssociatedWords([]);
            }
        } catch (error) {
            Logger.error("Error searching for associated words:", error);
            onShowSnackbar("Error searching for associated words", "error");
            setAssociatedWords([]);
        } finally {
            setLoading(false);
        }
    };

    // Normalize a single letter for comparison (French mode)
    const normalizeLetter = (letter, language) => {
        if (!letter || typeof letter !== 'string') return letter;
        if (language === 'french') {
            return normalizeFrenchWord(letter);
        }
        return letter.toLowerCase();
    };

    // Check if a word can fit in the puzzle and find its position
    const checkWordFitInPuzzle = async (words) => {
        if (!square || square.length === 0) return [];

        const emptySpaces = findEmptySpaces(square);
        const wordsWithPositions = [];
        const language = gridTpl?.language || 'english';

        for (const wordData of words) {
            const word = wordData.word.toUpperCase();

            // Find all possible positions for this word
            for (const space of emptySpaces) {
                if (space.pattern.length === word.length) {
                    // Check if word fits the pattern
                    let fits = true;
                    for (let i = 0; i < space.pattern.length; i++) {
                        const patternChar = space.pattern[i];
                        const wordChar = word[i];

                        // If pattern has a letter, word must match it (normalize for French)
                        if (patternChar !== '?') {
                            const normalizedPattern = normalizeLetter(patternChar, language);
                            const normalizedWord = normalizeLetter(wordChar, language);
                            if (normalizedPattern !== normalizedWord) {
                                fits = false;
                                break;
                            }
                        }
                    }

                    if (fits) {
                        // Generate positions for this word
                        const positions = generatePositions(
                            space.startRow,
                            space.startCol,
                            space.direction,
                            word.length,
                            word
                        );

                        wordsWithPositions.push({
                            ...wordData,
                            word: word,
                            positions: positions,
                            startRow: space.startRow,
                            startCol: space.startCol,
                            direction: space.direction,
                            pattern: space.pattern,
                            consecutivenessScore: space.consecutivenessScore
                        });
                        break; // Found a position, move to next word
                    }
                }
            }
        }

        return wordsWithPositions;
    };

    // Handle adding an associated word to the puzzle
    const handleAddAssociatedWord = (wordData) => {
        if (onWordAdd && wordData) {
            onWordAdd(wordData);
            // Clear the associated words list after adding
            setAssociatedWords([]);
            setSelectedAssociatedWord(null);
        }
    };

    // Handle associated word selection
    const handleAssociatedWordSelect = (wordData) => {
        setSelectedAssociatedWord(wordData);

        // Notify parent component about associated word selection for highlighting
        if (onWordSelect) {
            onWordSelect(wordData);
        }
    };

    // Pagination constants
    const WORDS_PER_PAGE = 10;
    const totalPages = Math.ceil(associatedWords.length / WORDS_PER_PAGE);
    const startIndex = (currentPage - 1) * WORDS_PER_PAGE;
    const endIndex = startIndex + WORDS_PER_PAGE;
    const paginatedWords = useMemo(() => {
        return associatedWords.slice(startIndex, endIndex);
    }, [associatedWords, startIndex, endIndex]);

    // Reset to page 1 when associatedWords change
    useEffect(() => {
        setCurrentPage(1);
    }, [associatedWords.length]);

    // Pagination handlers
    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    // Handle word not in puzzle selection - select the word for placement (keep puzzle word selected)
    const handleWordNotInPuzzleSelect = async (word) => {
        setSelectedWordForPlacement(word);
        // Don't change selectedWord - keep the puzzle word selected
        setSelectedAssociatedWord(null); // Clear associated word selection
        setLoading(true);
        setAssociatedWords([]);

        // Also notify parent component about word selection for definition lookup
        if (onWordSelect) {
            // Create a word object for consistency
            const wordObject = { text: word };
            onWordSelect(wordObject);
        }

        try {
            const language = gridTpl?.language || 'english';
            
            // Search for associated words using Dictionary API
            const result = await DictionaryApiService.getWordAssociations(word.toLowerCase(), language);
            const results = result.relatedWords || [];

            if (results && results.length > 0) {
                // Filter out words that are already used in the puzzle
                let usedWords = new Set();
                if (gridTpl?.filledCoordinates) {
                    try {
                        const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                        const words = filledCoordinates
                            .filter(w => w.text) // Filter out words without text
                            .map(w => w.text.toUpperCase());
                        usedWords = new Set(words);
                    } catch (error) {
                        Logger.error("Error parsing filledCoordinates:", error);
                    }
                }
                const filteredResults = results.filter(result =>
                    !usedWords.has(result.word.toUpperCase())
                );

                // Check if each word can fit in the puzzle
                const wordsWithPositions = await checkWordFitInPuzzle(filteredResults);
                setAssociatedWords(wordsWithPositions);
            } else {
                setAssociatedWords([]);
            }
        } catch (error) {
            Logger.error("Error searching for associated words:", error);
            onShowSnackbar("Error searching for associated words", "error");
            setAssociatedWords([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle open confirmation dialog
    const handleOpenPlacementDialog = () => {
        if (selectedWordForPlacement) {
            setWordToPlace(selectedWordForPlacement);
            setConfirmDialogOpen(true);
        }
    };

    // Handle confirmation and place word in puzzle
    const handleConfirmWordPlacement = async (updatedTitle) => {
        if (!wordToPlace) return;

        try {
            // Normalize French words by removing accents
            const language = gridTpl?.language || 'english';
            let normalizedWord = wordToPlace;
            if (language === 'french') {
                normalizedWord = normalizeFrenchWord(wordToPlace);
            } else {
                normalizedWord = wordToPlace.toLowerCase();
            }

            const wordLength = normalizedWord.length;
            const positions = [];

            // Get current filled positions to avoid conflicts
            const currentFilledPositions = new Set();

            // Check the actual puzzle grid for filled cells
            if (square) {
                Logger.log("Checking square array for filled cells:");
                for (let row = 0; row < gridSize; row++) {
                    for (let col = 0; col < gridSize; col++) {
                        const char = square[row][col];
                        Logger.log(`Cell (${row},${col}): "${char}" - isEmpty: ${!char || char === ""}`);
                        if (char && char !== "") {
                            currentFilledPositions.add(`${row}-${col}`);
                            Logger.log(`Marked (${row},${col}) as filled`);
                        }
                    }
                }
            }

            // Also check filledCoordinates from database
            if (gridTpl?.filledCoordinates) {
                try {
                    const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                    // Logger.log("Filled coordinates from database:", filledCoordinates);
                    filledCoordinates.forEach(w => {
                        if (w.positions) {
                            w.positions.forEach(pos => {
                                // Logger.log("your positions:", pos);

                                if (pos.value) {
                                    const posKey = `${pos.row}-${pos.col}`;
                                    currentFilledPositions.add(posKey);
                                    Logger.log(`Added from database: (${pos.row},${pos.col})`);
                                }
                            });
                        }
                    });
                } catch (error) {
                    Logger.error("Error parsing filled coordinates:", error);
                }
            }

            Logger.log("All filled positions:", Array.from(currentFilledPositions).sort());
            Logger.log("Current filled positions:", currentFilledPositions);

            // Count total empty cells
            let totalEmptyCells = 0;
            for (let row = 0; row < gridSize; row++) {
                for (let col = 0; col < gridSize; col++) {
                    const positionKey = `${row}-${col}`;
                    if (!currentFilledPositions.has(positionKey)) {
                        totalEmptyCells++;
                    }
                }
            }

            Logger.log(`Total empty cells available: ${totalEmptyCells}`);

            // Debug: Log the current state
            Logger.log("Word to place:", wordToPlace, "Length:", wordLength);
            Logger.log("Current filled positions:", Array.from(currentFilledPositions));
            Logger.log("Square state:", square);

            // Place characters from top-left to bottom-right in empty cells
            let charIndex = 0;

            // First, check if we have enough empty cells
            if (totalEmptyCells < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${totalEmptyCells} empty cells, need ${wordLength}`);
            }

            // Now place the characters
            for (let row = 0; row < gridSize && charIndex < wordLength; row++) {
                for (let col = 0; col < gridSize && charIndex < wordLength; col++) {
                    const positionKey = `${row}-${col}`;

                    // Check if this position is empty
                    if (!currentFilledPositions.has(positionKey)) {
                        positions.push({
                            row: row,
                            col: col,
                            value: normalizedWord[charIndex]
                        });
                        // Add to currentFilledPositions to prevent reuse
                        currentFilledPositions.add(positionKey);
                        charIndex++;
                        Logger.log(`Placed character ${charIndex}/${wordLength}: ${normalizedWord[charIndex - 1]} at (${row}, ${col})`);
                    }
                }
            }

            Logger.log("Final charIndex:", charIndex, "Word length:", wordLength);
            Logger.log("Positions placed:", positions);

            // Make sure we placed all characters
            if (charIndex < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${charIndex} empty cells, need ${wordLength}`);
            }

            // Create word data with sequential positions
            const wordData = {
                word: normalizedWord,
                text: normalizedWord,
                positions: positions,
                direction: 'scattered',
                length: normalizedWord.length,
                startRow: positions[0]?.row || 0,
                startCol: positions[0]?.col || 0
            };

            // Add the word to the puzzle and wait for it to complete
            if (onWordAdd) {
                try {
                    // Call onWordAdd and await if it returns a promise
                    const addResult = onWordAdd(wordData);
                    if (addResult && typeof addResult.then === 'function') {
                        await addResult;
                    } else {
                        // Give a small delay to ensure the word is processed if not async
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                    
                    // Update puzzle type to HIDDEN_MESSAGE when word is placed, including title and hiddenMessage
                    if (gridTpl?.id) {
                        try {
                            const currentState = gridTpl.state || DRAFT;
                            const titleToUse = updatedTitle !== undefined ? updatedTitle : (gridTpl.title || '');
                            // Use the normalized word (lowercase) as the hidden message
                            await AdminGridBoardService.updateHwTplState(gridTpl.id, currentState, 'HIDDEN_MESSAGE', null, titleToUse, normalizedWord);
                            
                            // Trigger refresh to update the puzzle data with the new type and hiddenMessage
                            if (onRefresh) {
                                onRefresh();
                            }
                            
                            onShowSnackbar(`Word "${wordToPlace}" placed in puzzle. Puzzle type set to HIDDEN_MESSAGE.`, "success");
                        } catch (error) {
                            Logger.error("Error updating puzzle type to HIDDEN_MESSAGE:", error);
                            onShowSnackbar(`Word "${wordToPlace}" placed in puzzle, but failed to update puzzle type`, "warning");
                        }
                    } else {
                        onShowSnackbar(`Word "${wordToPlace}" placed in puzzle`, "success");
                    }
                } catch (error) {
                    Logger.error("Error adding word to puzzle:", error);
                    onShowSnackbar("Error adding word to puzzle: " + error.message, "error");
                }
            }

        } catch (error) {
            Logger.error("Error placing word in puzzle:", error);
            onShowSnackbar("Error placing word in puzzle: " + error.message, "error");
        } finally {
            setConfirmDialogOpen(false);
            setWordToPlace(null);
        }
    };

    // Handle add all words not in use to empty cells (calls backend fill-more API)
    const handleAddAllWordsNotInUse = async () => {
        if (!gridTpl?.id) {
            onShowSnackbar("No puzzle available", "warning");
            return;
        }
        if (!wordsNotInUse.length || !emptyCellsCount) {
            onShowSnackbar("No words to add or no empty cells", "warning");
            return;
        }

        try {
            const result = await AdminGridBoardService.fillMore(gridTpl.id);
            if (onRefresh) onRefresh();
            const wordsPlaced = result?.wordsPlaced ?? result?.addedCount ?? result?.wordsAdded ?? 0;
            if (wordsPlaced > 0) {
                onShowSnackbar(`${wordsPlaced} word(s) added to puzzle`, "success");
            } else {
                onShowSnackbar(result?.message ?? "No additional words placed", "info");
            }
        } catch (err) {
            Logger.error("Error adding words:", err);
            onShowSnackbar("Error adding words: " + (err?.message || err), "error");
        } finally {
            setAddAllWordsDialogOpen(false);
        }
    };

    // Calculate actual filled cells by considering unique coordinates
    const calculateFilledCells = useCallback(() => {
        if (!gridTpl?.filledCoordinates) return 0;
        try {
            const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
            const uniqueCells = new Set();

            filledCoordinates.forEach(word => {
                if (word.text && word.positions) {
                    // Add each position coordinate to the set
                    word.positions.forEach(pos => {
                        uniqueCells.add(`${pos.row}-${pos.col}`);
                    });
                }
            });

            return uniqueCells.size;
        } catch (error) {
            Logger.error("Error calculating filled cells:", error);
            return 0;
        }
    }, [gridTpl?.filledCoordinates]);

    const emptyCellsCount = gridCellCount - calculateFilledCells();

    // Handle Bedrock word search
    const handleBedrockWordSearch = useCallback(async () => {
        if (!gridTpl?.id || emptyCellsCount <= 0) {
            onShowSnackbar("Cannot search for words: invalid puzzle or no empty cells", "error");
            return;
        }

        setLoadingBedrock(true);
        setBedrockCandidates([]);

        try {
            const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${gridTpl.id}/${emptyCellsCount}/find-word/bedrock`);
            
            if (result && result.words && Array.isArray(result.words)) {
                setBedrockCandidates(result.words);
                if (result.words.length > 0) {
                    onShowSnackbar(`Found ${result.words.length} word(s) from ${result.source || 'Bedrock'}`, "success");
                } else {
                    onShowSnackbar("No words found for this length", "info");
                }
            } else {
                setBedrockCandidates([]);
                onShowSnackbar("No words found", "info");
            }
        } catch (error) {
            Logger.error("Error fetching Bedrock words:", error);
            onShowSnackbar("Error fetching words from Bedrock. Please try again.", "error");
            setBedrockCandidates([]);
        } finally {
            setLoadingBedrock(false);
        }
    }, [gridTpl?.id, emptyCellsCount, onShowSnackbar]);

    // Handle cleanup
    const handleCleanUp = async () => {
        try {
            await AdminGridBoardService.cleanUp(gridTpl);
            onShowSnackbar("Puzzle cleaned up successfully", "success");
            // Refresh the page after successful cleanup
            window.location.reload();
        } catch (error) {
            Logger.error("Error cleaning up puzzle:", error);
            onShowSnackbar("Error cleaning up puzzle", "error");
        }
    };

    // Handle delete selected word - show confirmation dialog
    const handleDeleteSelectedWord = () => {
        if (!selectedWord || !gridTpl?.id) {
            onShowSnackbar("Please select a word to delete", "warning");
            return;
        }

        // Set the word to delete and show confirmation dialog
        setWordToDelete(selectedWord);
        setDeleteWordDialogOpen(true);
    };

    // Handle confirmation and delete word
    const handleConfirmDeleteWord = async () => {
        if (!wordToDelete || !gridTpl?.id) return;

        try {
            // Get current filled coordinates and remove the selected word
            const filledCoords = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            const updatedCoords = filledCoords.filter(coord =>
                coord.text && coord.text.toUpperCase() !== wordToDelete.toUpperCase()
            );

            // Call API to update the puzzle
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedCoords, null);

            onShowSnackbar(`Word "${wordToDelete}" removed successfully`, "success");

            // Clear selected word
            setSelectedWord(null);
            setSelectedAssociatedWord(null);
            setAssociatedWords([]);
            setCandidateWords([]);

            // Notify parent to refresh puzzle data
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            Logger.error("Error deleting word:", error);
            onShowSnackbar("Error deleting word", "error");
        } finally {
            setDeleteWordDialogOpen(false);
            setWordToDelete(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            {/* Show no empty spaces message if there are no empty spaces */}
            {emptySpaces.length === 0 && square && square.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="info" sx={{ maxWidth: '100%' }}>
                        There are no empty spaces in this puzzle. All cells are filled with letters.
                    </Alert>
                </Box>
            )}
            
            {/* Puzzle Words Section - Always show */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                        Words in Puzzle ({puzzleWords.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Delete selected word from puzzle">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleDeleteSelectedWord}
                                    disabled={!selectedWord}
                                    sx={{
                                        '&:disabled': {
                                            opacity: 0.3
                                        }
                                    }}
                                >
                                    <DeleteIcon fontSize="small" color={selectedWord ? "error" : "action"} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={handleCleanUp}
                            title="Clean up puzzle, remove garbage data (it doesn't affect the valid words), leave only words in puzzle"
                        >
                            <CleaningServicesIcon fontSize="small" color="action" />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                    {puzzleWords.length > 0 ? (
                        puzzleWords
                            .filter(word => word) // Filter out empty/null words
                            .map((word, index) => {
                                const isHighlighted = selectedWord === word || (highlightedWordFromPreview && String(word).toLowerCase() === String(highlightedWordFromPreview).toLowerCase());
                                return (
                                    <Chip
                                        key={index}
                                        label={word}
                                        onClick={() => handleWordSelect(word)}
                                        color={isHighlighted ? "primary" : "default"}
                                        variant={isHighlighted ? "filled" : "outlined"}
                                        size="small"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                );
                            })
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No words in puzzle yet
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Words Not In Use Section */}
            {emptySpaces.length > 0 && (isFrenchPuzzle || wordsNotInUse.length > 0) && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="subtitle2">
                                Words Not In Use ({isFrenchPuzzle
                                    ? "—"
                                    : filterWordsNotInUseByLength
                                      ? wordsNotInUse.filter(word => word.length === emptyCellsCount).length
                                      : wordsNotInUse.length})
                            </Typography>
                            {!isFrenchPuzzle ? (
                                <Tooltip title="Add words not in use to empty cells">
                                    <IconButton
                                        size="small"
                                        onClick={() => setAddAllWordsDialogOpen(true)}
                                        disabled={emptyCellsCount <= 0 || wordsNotInUse.length === 0}
                                        sx={{ minWidth: 'auto', '&:hover': { backgroundColor: 'action.hover' } }}
                                    >
                                        <AddTaskIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!isFrenchPuzzle ? (
                            <Typography
                                variant="caption"
                                color="error"
                                onClick={() => setFilterWordsNotInUseByLength(!filterWordsNotInUseByLength)}
                                sx={{
                                    fontWeight: 'bold',
                                    letterSpacing: 0.5,
                                    fontSize: '0.75rem',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: (theme) => filterWordsNotInUseByLength 
                                        ? theme.palette.error.main 
                                        : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                    color: filterWordsNotInUseByLength ? 'white' : 'error.main',
                                    border: (theme) => `1px solid ${theme.palette.error.main}`,
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: (theme) => theme.palette.error.main,
                                        color: 'white'
                                    }
                                }}
                            >
                                Empty: {emptyCellsCount}
                            </Typography>
                            ) : null}
                            {!isFrenchPuzzle && selectedWordForPlacement && wordsNotInUse.includes(selectedWordForPlacement) && (
                                <IconButton
                                    size="small"
                                    onClick={handleOpenPlacementDialog}
                                    title={`Place "${selectedWordForPlacement}" into the puzzle grid as part of a hidden message`}
                                    sx={{
                                        minWidth: 'auto',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    <AddIcon fontSize="small" color="primary" sx={{ outline: '1px solid', borderRadius: '50%' }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                    {isFrenchPuzzle ? (
                        <WordsNotInUseFrenchNotice />
                    ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                        {(() => {
                            const filteredWords = filterWordsNotInUseByLength 
                                ? wordsNotInUse.filter(word => word.length === emptyCellsCount)
                                : wordsNotInUse;
                            const sortedWords = [...filteredWords].sort((a, b) => a.length - b.length);
                            return sortedWords.length > 0 ? (
                                sortedWords.map((word, index) => (
                                    <Chip
                                        key={index}
                                        label={
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>{word}</span>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 18,
                                                        height: 18,
                                                        borderRadius: '50%',
                                                        bgcolor: 'action.selected',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {word.length}
                                                </Box>
                                            </Box>
                                        }
                                        onClick={() => handleWordNotInPuzzleSelect(word)}
                                        color={selectedWordForPlacement === word ? "secondary" : "default"}
                                        variant={selectedWordForPlacement === word ? "filled" : "outlined"}
                                        size="small"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {filterWordsNotInUseByLength 
                                        ? `No words found with length = ${emptyCellsCount} characters`
                                        : "No words not in use"}
                                </Typography>
                            );
                        })()}
                    </Box>
                    )}
                    
                    {/* Bedrock Candidates Section (available for French puzzles too) */}
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                    Bedrock AI Candidates ({filterBedrockByLength 
                                        ? bedrockCandidates.filter(word => word.length === emptyCellsCount).length 
                                        : bedrockCandidates.length})
                                </Typography>
                                <Tooltip title="Find words using Bedrock AI">
                                    <IconButton
                                        size="small"
                                        onClick={handleBedrockWordSearch}
                                        disabled={loadingBedrock || emptyCellsCount <= 0}
                                        sx={{
                                            minWidth: 'auto',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        {loadingBedrock ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <AutoAwesomeIcon fontSize="small" color="primary" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="error"
                                    onClick={() => setFilterBedrockByLength(!filterBedrockByLength)}
                                    sx={{
                                        fontWeight: 'bold',
                                        letterSpacing: 0.5,
                                        fontSize: '0.75rem',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        backgroundColor: (theme) => filterBedrockByLength 
                                            ? theme.palette.error.main 
                                            : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                        color: filterBedrockByLength ? 'white' : 'error.main',
                                        border: (theme) => `1px solid ${theme.palette.error.main}`,
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: (theme) => theme.palette.error.main,
                                            color: 'white'
                                        }
                                    }}
                                >
                                    Empty: {emptyCellsCount}
                                </Typography>
                                {selectedWordForPlacement && bedrockCandidates.includes(selectedWordForPlacement) && (
                                    <IconButton
                                        size="small"
                                        onClick={handleOpenPlacementDialog}
                                        title={`Place "${selectedWordForPlacement}" into the puzzle grid as part of a hidden message`}
                                        sx={{
                                            minWidth: 'auto',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <AddIcon fontSize="small" color="primary" sx={{ outline: '1px solid', borderRadius: '50%' }} />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                        {(() => {
                            const filteredCandidates = filterBedrockByLength 
                                ? bedrockCandidates.filter(word => word.length === emptyCellsCount)
                                : bedrockCandidates;
                            return filteredCandidates.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                                    {filteredCandidates.map((word, index) => (
                                        <Chip
                                            key={index}
                                            label={word}
                                            onClick={() => handleWordNotInPuzzleSelect(word)}
                                            color={selectedWordForPlacement === word ? "secondary" : "default"}
                                            variant={selectedWordForPlacement === word ? "filled" : "outlined"}
                                            size="small"
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {filterBedrockByLength 
                                        ? `No words found with length = ${emptyCellsCount} characters`
                                        : "Click the magic icon to find words using Bedrock AI"}
                                </Typography>
                            );
                        })()}
                    </Box>
                </Box>
            )}

            {/* Only show candidates section if there are empty spaces */}
            {emptySpaces.length > 0 && (
                <>

                    {/* Words Not In Use Section - Show candidates based on synonyms/associations */}
                    {selectedWord && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                    Candidates for scattered placement
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        onClick={() => setFilterByLength(!filterByLength)}
                                        sx={{
                                            fontWeight: 'bold',
                                            letterSpacing: 0.5,
                                            fontSize: '0.75rem',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            backgroundColor: (theme) => filterByLength 
                                                ? theme.palette.error.main 
                                                : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                            color: filterByLength ? 'white' : 'error.main',
                                            border: (theme) => `1px solid ${theme.palette.error.main}`,
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: (theme) => theme.palette.error.main,
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Empty: {emptyCellsCount}
                                    </Typography>
                                    {selectedWordForPlacement && (
                                        <IconButton
                                            size="small"
                                            onClick={handleOpenPlacementDialog}
                                            title={`Place "${selectedWordForPlacement}" into the puzzle grid as part of a hidden message`}
                                            sx={{
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <AddIcon fontSize="small" color="primary" sx={{ outline: '1px solid', borderRadius: '50%' }} />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                                {loadingCandidates ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Searching for candidates...
                                    </Typography>
                                ) : (() => {
                                    const filteredCandidates = filterByLength 
                                        ? candidateWords.filter(candidate => candidate.word.length === emptyCellsCount)
                                        : candidateWords;
                                    return filteredCandidates.length > 0 ? (
                                        filteredCandidates.map((candidate, index) => (
                                            <Chip
                                                key={index}
                                                label={`${candidate.word.toLowerCase()} (${candidate.word.length})`}
                                                size="small"
                                                variant={selectedWordForPlacement === candidate.word ? "filled" : "outlined"}
                                                color={selectedWordForPlacement === candidate.word ? "primary" : "warning"}
                                                onClick={() => handleWordNotInPuzzleSelect(candidate.word)}
                                                sx={{ cursor: 'pointer' }}
                                                title={`${candidate.type} of "${candidate.source}" - Click to select for placement`}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {filterByLength 
                                                ? `No candidate words found with length = ${emptyCellsCount} characters`
                                                : `No candidate words found with length ≤ ${emptyCellsCount} characters`}
                                        </Typography>
                                    );
                                })()}
                            </Box>
                        </Box>
                    )}

                    {/* Associated Words Section */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">
                                Associated Words {loading && "(Searching...)"}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                        fontWeight: 'bold',
                                        letterSpacing: 0.5,
                                        fontSize: '0.75rem',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        backgroundColor: (theme) => theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                        color: 'error.main',
                                        border: (theme) => `1px solid ${theme.palette.error.main}`,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Empty: {emptyCellsCount}
                                </Typography>
                                {associatedWords.length > 0 && (
                                    <>
                                    <Tooltip title="Previous Page">
                                        <span>
                                            <IconButton
                                                onClick={handlePreviousPage}
                                                disabled={currentPage === 1}
                                                size="small"
                                            >
                                                <ChevronLeftIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                                        {currentPage} / {totalPages}
                                    </Typography>
                                    <Tooltip title="Next Page">
                                        <span>
                                            <IconButton
                                                onClick={handleNextPage}
                                                disabled={currentPage === totalPages}
                                                size="small"
                                            >
                                                <ChevronRightIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                            </Box>
                        </Box>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <Typography>Searching for associated words...</Typography>
                            </Box>
                        ) : associatedWords.length > 0 ? (
                            <List sx={{ flex: 1, overflow: 'auto' }}>
                                {paginatedWords.map((wordData, index) => (
                                    <GridCandEntry
                                        key={index}
                                        candidate={wordData}
                                        isSelected={selectedAssociatedWord?.word === wordData.word}
                                        onWordSelect={handleAssociatedWordSelect}
                                        onWordAdd={handleAddAssociatedWord}
                                    />
                                ))}
                            </List>
                        ) : selectedWord ? (
                            <Alert severity="info">
                                No associated words found that can fit in the puzzle
                            </Alert>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                Select a word from the puzzle to find associated words
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            {/* Confirmation Dialog for Word Placement */}
            <GirdCanAssocConfirmDialog
                open={confirmDialogOpen}
                setOpen={(open) => {
                    setConfirmDialogOpen(open);
                    if (!open) onClearPlacementWordFromFill?.();
                }}
                word={wordToPlace}
                title={gridTpl?.title || ''}
                gridTplId={gridTpl?.id}
                currentState={gridTpl?.state || DRAFT}
                puzzleType="HIDDEN_MESSAGE"
                onConfirm={handleConfirmWordPlacement}
                language={gridTpl?.language || 'english'}
                emptyCellCount={emptyCellsCount}
            />

            {/* Confirmation Dialog for Word Deletion */}
            <ConfirmDialog
                open={deleteWordDialogOpen}
                setOpen={(open) => {
                    setDeleteWordDialogOpen(open);
                    if (!open) {
                        setWordToDelete(null);
                    }
                }}
                title="Delete Word"
                message={`Would you like to delete the word "${wordToDelete}"?`}
                targetName=""
                targetId=""
                callback={handleConfirmDeleteWord}
            />

            {/* Confirmation Dialog for Add All Words Not In Use */}
            <ConfirmDialog
                open={addAllWordsDialogOpen}
                setOpen={setAddAllWordsDialogOpen}
                title="Add Words to Puzzle"
                message="Would you like to add words in 'words not in use' list to the puzzle?"
                targetName=""
                targetId=""
                callback={handleAddAllWordsNotInUse}
            />
        </Box>
    );
}

